/**
 * Wishlist Manager — localStorage + Server Sync (Phase 2)
 *
 * Dependencies: None (standalone)
 * Global vars used: window.themeVariables.settings.moneyFormat
 *                   window.themeVariables.wishlistSync  (customerId, hmac, url)
 *
 * Data structure in localStorage (key: 'padelneeds_wishlist'):
 * {
 *   "1234567890": {
 *     "handle": "product-handle",
 *     "title": "Product Title",
 *     "image": "https://cdn.shopify.com/...",
 *     "price": 5000,
 *     "url": "/products/product-handle",
 *     "variantId": "9876543210",
 *     "addedAt": "2024-01-01T00:00:00.000Z"
 *   }
 * }
 */

const STORAGE_KEY = 'padelneeds_wishlist';
const SYNC_AT_KEY = 'padelneeds_wishlist_synced_at'; // ISO timestamp of last successful server sync
const MAX_WISHLIST_ITEMS = 500;

// ── localStorage helpers ─────────────────────────────────────

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveWishlist(wishlist) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
  } catch {
    // Safari private mode — silently fail
  }
}

function addToWishlist(productId, data) {
  const wishlist = getWishlist();
  wishlist[productId] = { ...data, addedAt: new Date().toISOString() };
  saveWishlist(wishlist);
  triggerDebouncedSync();
}

function removeFromWishlist(productId) {
  const wishlist = getWishlist();
  delete wishlist[productId];
  saveWishlist(wishlist);
  triggerDebouncedSync();
}

function isInWishlist(productId) {
  return getWishlist().hasOwnProperty(String(productId));
}

function getWishlistCount() {
  return Object.keys(getWishlist()).length;
}

// ── Money formatting ─────────────────────────────────────────

function formatMoney(cents) {
  const format = window.themeVariables?.settings?.moneyFormat || '{{amount}} SAR';
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.round(cents / 100).toString();
  const amountWithComma = amount.replace('.', ',');

  return format
    .replace('{{amount_with_comma_separator}}', amountWithComma)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
    .replace('{{amount}}', amount);
}

// ── Translation helper ───────────────────────────────────────

function t(key) {
  const strings = window.themeVariables?.strings || {};
  return strings[key] || key;
}

// ── UI Updates ───────────────────────────────────────────────

function updateAllButtons() {
  document.querySelectorAll('wishlist-button').forEach((el) => {
    const productId = el.dataset.productId;
    const inWishlist = isInWishlist(productId);
    el.classList.toggle('is-active', inWishlist);

    const toggle = el.querySelector('.wishlist-toggle');
    if (toggle) {
      toggle.setAttribute('data-label', inWishlist ? t('wishlistRemove') : t('wishlistAdd'));
      toggle.setAttribute('aria-pressed', inWishlist);

      const label = toggle.querySelector('.wishlist-toggle__label');
      if (label) {
        label.textContent = inWishlist ? t('wishlistAdded') : t('wishlistAdd');
      }
    }
  });

  // Update cart save buttons (heart icon below Remove)
  document.querySelectorAll('[data-wishlist-cart-save]').forEach((btn) => {
    const productId = btn.dataset.wishlistProductId;
    btn.classList.toggle('is-active', isInWishlist(productId));
  });
}

function updateHeaderCount() {
  const count = getWishlistCount();
  // Cap visual display at 99+ to prevent badge overflow on large wishlists
  const displayCount = count > 99 ? '99+' : count;
  document.querySelectorAll('wishlist-count').forEach((el) => {
    const visibleSpan = el.querySelector('[aria-hidden]');
    const srSpan = el.querySelector('[data-wishlist-sr]');
    if (visibleSpan) visibleSpan.textContent = displayCount;
    // Update SR text with translated count string (or empty when zero)
    if (srSpan) {
      srSpan.textContent = count > 0
        ? t('wishlistItemsCount').replace('{{ count }}', String(count))
        : '';
    }
    el.classList.toggle('opacity-0', count === 0);
  });
}

function updateUI() {
  updateAllButtons();
  updateHeaderCount();
}

// ── Toast ────────────────────────────────────────────────────

let toastTimer = null;

function showToast(message) {
  const toast = document.querySelector('wishlist-toast');
  if (!toast) return;

  const msgEl = toast.querySelector('.wishlist-toast__message');
  if (msgEl) msgEl.textContent = message;

  toast.removeAttribute('hidden');
  // Force reflow before adding class
  toast.offsetHeight;
  toast.classList.add('is-visible');

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.setAttribute('hidden', ''), 400);
  }, 3000);
}

// ── Wishlist Page (Section Rendering API) ────────────────────

async function renderWishlistPage() {
  const grid = document.querySelector('[data-wishlist-grid]');
  const empty = document.querySelector('[data-wishlist-empty]');
  const loading = document.querySelector('[data-wishlist-loading]');
  const countEl = document.querySelector('[data-wishlist-page-count]');

  if (!grid) return; // Not on wishlist page

  const wishlist = getWishlist();
  const entries = Object.entries(wishlist);

  if (entries.length === 0) {
    if (loading) loading.setAttribute('hidden', '');
    if (empty) empty.removeAttribute('hidden');
    if (countEl) countEl.textContent = t('wishlistItemsCount').replace('{{ count }}', '0');
    return;
  }

  if (countEl) {
    countEl.textContent = t('wishlistItemsCount').replace('{{ count }}', String(entries.length));
  }

  // Fetch rendered cards via Section Rendering API (parallel)
  const fetchPromises = entries.map(async ([productId, item]) => {
    try {
      const rootUrl = window.themeVariables?.routes?.root_url || '/';
      const fetchUrl = `${rootUrl}/products/${item.handle}?section_id=wishlist-card-renderer`.replace('//', '/');
      const res = await fetch(fetchUrl);
      if (!res.ok) {
        // Product might have been deleted — remove from wishlist
        removeFromWishlist(productId);
        return null;
      }
      const fullHtml = await res.text();
      // Strip Shopify section wrappers so product-card is a direct grid child for subgrid alignment
      const parser = new DOMParser();
      const doc = parser.parseFromString(fullHtml, 'text/html');
      const card = doc.querySelector('.wishlist-rendered-card');
      if (!card) {
        removeFromWishlist(productId);
        return null;
      }
      return { productId, html: card.outerHTML };
    } catch {
      // Network error — render a minimal fallback card
      return { productId, html: renderFallbackCard(item, productId) };
    }
  });

  const results = await Promise.all(fetchPromises);
  const validCards = results.filter(Boolean);

  if (loading) loading.setAttribute('hidden', '');

  if (validCards.length === 0) {
    if (empty) empty.removeAttribute('hidden');
    // Update count since some products may have been removed
    if (countEl) countEl.textContent = t('wishlistItemsCount').replace('{{ count }}', '0');
    return;
  }

  // Build the grid HTML
  grid.innerHTML = validCards.map((card) => card.html).join('');
  grid.removeAttribute('hidden');

  // Update count in case some products were removed during fetch
  if (countEl) {
    const currentCount = getWishlistCount();
    countEl.textContent = t('wishlistItemsCount').replace('{{ count }}', String(currentCount));
  }
}

/**
 * Fallback card — only used when Section Rendering API fails (network error).
 * Uses cached data from localStorage with Impact theme's product-card markup.
 */
function renderFallbackCard(item, productId) {
  return `
    <div class="wishlist-rendered-card" data-product-id="${productId}">
      <product-card handle="${item.handle || ''}" class="product-card product-card--blends">
        <div class="product-card__figure">
          <a href="${item.url || '#'}" data-instant>
            ${item.image ? `<img src="${item.image}" alt="${item.title || ''}" loading="lazy" class="product-card__image product-card__image--primary aspect-natural">` : ''}
          </a>
        </div>
        <div class="product-card__info">
          <div class="v-stack gap-0.5 w-full">
            <span class="product-card__title"><a href="${item.url || '#'}" class="bold" data-instant>${item.title || ''}</a></span>
            ${item.price ? `<price-list class="price-list"><sale-price class="text-subdued"><span class="sr-only">Sale price</span>${formatMoney(item.price)}</sale-price></price-list>` : ''}
          </div>
        </div>
      </product-card>
      <div class="wishlist-card-remove-overlay">
        <button type="button" class="wishlist-card-remove-btn" data-wishlist-remove="${productId}" aria-label="${t('wishlistRemoveItem')}" style="opacity:1;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  `;
}

// ── Server Sync ───────────────────────────────────────────────
//
// Strategy (server wins after first sync):
//   - Page load: GET server → merge with localStorage → push merged back
//   - First sync ever: union (localStorage ∪ server) so no data lost
//   - Subsequent syncs: server is truth + keep any local items added since lastSync
//   - add/remove: debounced POST (2s) — batches rapid changes into one request
//   - If server unavailable: graceful degradation (localStorage only, no error shown)

let _cachedMetafieldId = null; // Shopify metafield ID, cached after first GET/POST
let _syncDebounce = null;

/**
 * fetch() with exponential backoff retry.
 * Retries on network errors or 5xx responses.
 * Returns the Response on success, null on total failure.
 */
async function fetchWithRetry(url, options = {}, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, options);
      // Don't retry 4xx (client errors — hmac wrong, rate limited, etc.)
      if (res.ok || (res.status >= 400 && res.status < 500)) return res;
    } catch {
      if (attempt === retries) return null;
    }
    // Exponential backoff: 500ms → 1000ms
    await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
  }
  return null;
}

/**
 * GET wishlist items from Cloudflare Worker → Shopify metafield.
 * Returns items object on success, null on failure (graceful degradation).
 */
async function fetchServerWishlist(cfg) {
  const { customerId, hmac, url } = cfg;
  if (!url || url === 'WISHLIST_SYNC_URL_PLACEHOLDER') return null;

  try {
    const res = await fetchWithRetry(
      `${url}/wishlist?customer_id=${encodeURIComponent(customerId)}&hmac=${encodeURIComponent(hmac)}`
    );
    if (!res || !res.ok) return null;

    const data = await res.json();
    // Cache metafield ID for future PUT requests (avoids extra GET calls)
    if (data.metafieldId) _cachedMetafieldId = data.metafieldId;
    return data.items || {};
  } catch {
    return null;
  }
}

/**
 * POST wishlist items to Cloudflare Worker → Shopify metafield.
 * Fails silently (localStorage is always up to date regardless).
 */
async function pushToServer(cfg, items) {
  const { customerId, hmac, url } = cfg;
  if (!url || url === 'WISHLIST_SYNC_URL_PLACEHOLDER') return;

  try {
    const res = await fetchWithRetry(`${url}/wishlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: customerId,
        hmac,
        items,
        metafield_id: _cachedMetafieldId,
      }),
    });
    if (res && res.ok) {
      const data = await res.json();
      if (data.metafieldId) _cachedMetafieldId = data.metafieldId;
    }
  } catch {
    // Graceful degradation — localStorage stays as-is
  }
}

/**
 * Merge strategy: server wins, but preserve local items added after lastSyncedAt.
 *
 * - All server items are kept (server is truth)
 * - Local items NOT on server: kept only if added AFTER lastSyncedAt
 *   (i.e. added locally since last sync, not removed from another device)
 * - First sync (lastSyncedAt null): union — all local items kept
 */
function mergeWishlists(local, server, lastSyncedAt) {
  const merged = { ...server };
  for (const [id, item] of Object.entries(local)) {
    if (merged[id]) continue; // Server has it — server version wins
    // Keep local item only if it was added after the last sync (new local addition)
    if (!lastSyncedAt || (item.addedAt && item.addedAt > lastSyncedAt)) {
      merged[id] = item;
    }
    // If addedAt <= lastSyncedAt and not on server → it was deleted from another device → drop it
  }
  return merged;
}

/**
 * Main sync function — called on page load with 1.5s lazy delay.
 * 1. GET server items
 * 2. Merge with localStorage (server wins strategy)
 * 3. Enforce MAX_WISHLIST_ITEMS (remove oldest)
 * 4. Save merged to localStorage + push to server
 */
async function syncWithServer() {
  const cfg = window.themeVariables?.wishlistSync;
  if (!cfg?.customerId || !cfg?.url || cfg.url === 'WISHLIST_SYNC_URL_PLACEHOLDER') return;

  // Lazy delay — don't block page render
  await new Promise((r) => setTimeout(r, 1500));

  const serverItems = await fetchServerWishlist(cfg);
  if (serverItems === null) return; // Server unavailable — stay with localStorage

  const localItems = getWishlist();
  const lastSyncedAt = localStorage.getItem(SYNC_AT_KEY);
  const merged = mergeWishlists(localItems, serverItems, lastSyncedAt);

  // Enforce max items — remove oldest by addedAt
  const keys = Object.keys(merged);
  if (keys.length > MAX_WISHLIST_ITEMS) {
    keys
      .map((k) => ({ id: k, addedAt: merged[k].addedAt || '' }))
      .sort((a, b) => a.addedAt.localeCompare(b.addedAt)) // oldest first
      .slice(0, keys.length - MAX_WISHLIST_ITEMS)
      .forEach(({ id }) => delete merged[id]);
  }

  saveWishlist(merged);
  localStorage.setItem(SYNC_AT_KEY, new Date().toISOString());
  updateUI();
  renderWishlistPage(); // Re-render wishlist page if user is on it after sync

  // Push merged state back to server (creates metafield if it doesn't exist yet)
  await pushToServer(cfg, merged);
}

/**
 * Debounced push — called after every add/remove.
 * Waits 2s before pushing so rapid changes are batched into one request.
 */
function triggerDebouncedSync() {
  const cfg = window.themeVariables?.wishlistSync;
  if (!cfg?.customerId) return; // Not logged in — no sync needed

  clearTimeout(_syncDebounce);
  _syncDebounce = setTimeout(async () => {
    await pushToServer(cfg, getWishlist());
    localStorage.setItem(SYNC_AT_KEY, new Date().toISOString());
  }, 2000);
}

// ── Event Handlers ───────────────────────────────────────────

function handleToggle(el) {
  const productId = el.dataset.productId;

  if (isInWishlist(productId)) {
    removeFromWishlist(productId);
    el.classList.remove('is-active');
    showToast(t('wishlistRemoved'));
  } else {
    addToWishlist(productId, {
      handle: el.dataset.productHandle,
      title: el.dataset.productTitle,
      image: el.dataset.productImage,
      price: parseInt(el.dataset.productPrice, 10) || 0,
      url: el.dataset.productUrl,
      variantId: el.dataset.variantId,
    });
    el.classList.add('is-active', 'is-animating');
    setTimeout(() => el.classList.remove('is-animating'), 500);
    showToast(t('wishlistAdded'));
  }

  updateUI();
}

async function handleAddToCart(variantId) {
  try {
    const rootUrl = window.themeVariables?.routes?.root_url || '/';
    const fetchUrl = `${rootUrl}cart/add.js`.replace('//', '/');
    const res = await fetch(fetchUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    });

    if (res.ok) {
      showToast(t('wishlistAddedToCart'));
      // Trigger Impact theme's cart update
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } else {
      showToast(t('wishlistCartError'));
    }
  } catch {
    showToast(t('wishlistCartError'));
  }
}

function handleRemoveFromPage(productId) {
  removeFromWishlist(productId);

  // Animate card out — now uses .wishlist-rendered-card wrapper
  const card = document.querySelector(`.wishlist-rendered-card[data-product-id="${productId}"]`);
  if (card) {
    card.classList.add('is-removing');
    setTimeout(() => {
      card.remove();
      // Check if grid is now empty
      const grid = document.querySelector('[data-wishlist-grid]');
      if (grid && grid.children.length === 0) {
        grid.setAttribute('hidden', '');
        const empty = document.querySelector('[data-wishlist-empty]');
        if (empty) empty.removeAttribute('hidden');
      }
    }, 300);
  }

  updateUI();
  showToast(t('wishlistRemoved'));

  // Update count on page
  const countEl = document.querySelector('[data-wishlist-page-count]');
  if (countEl) {
    const count = getWishlistCount();
    countEl.textContent = t('wishlistItemsCount').replace('{{ count }}', String(count));
  }
}

// ── Cart Remove → Confirm Dropdown ───────────────────────────

let _dropdown = null;
let _dropdownData = null;

/**
 * Returns the singleton dropdown element, creating it on first call.
 * Appended to document.body so it survives cart drawer re-renders.
 * Uses fixed positioning near the triggering Remove link.
 */
function getDropdown() {
  if (_dropdown) return _dropdown;

  const el = document.createElement('div');
  el.id = 'wishlist-cart-dropdown';
  el.className = 'wl-dropdown';
  el.setAttribute('hidden', '');
  el.innerHTML = `
    <button type="button" class="wl-dropdown__btn wl-dropdown__btn--save" data-action="save">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
      <span>${t('wishlistSaveBeforeRemove')}</span>
    </button>
    <button type="button" class="wl-dropdown__btn wl-dropdown__btn--delete" data-action="delete">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      </svg>
      <span>${t('wishlistDeletePermanently')}</span>
    </button>
  `;
  document.body.appendChild(el);

  _dropdown = el;
  return el;
}

function showDropdown(anchor, data) {
  const el = getDropdown();
  _dropdownData = data;

  // Position below the anchor
  const rect = anchor.getBoundingClientRect();
  el.style.top = (rect.bottom + 6) + 'px';
  el.style.left = rect.left + 'px';
  el.removeAttribute('hidden');

  // Adjust if off-screen
  requestAnimationFrame(() => {
    const dr = el.getBoundingClientRect();
    if (dr.right > window.innerWidth - 12) {
      el.style.left = Math.max(12, window.innerWidth - dr.width - 12) + 'px';
    }
    if (dr.bottom > window.innerHeight - 12) {
      el.style.top = (rect.top - dr.height - 6) + 'px';
    }
  });
}

function hideDropdown() {
  if (_dropdown) _dropdown.setAttribute('hidden', '');
  _dropdownData = null;
}

function isDropdownOpen() {
  return _dropdown && !_dropdown.hasAttribute('hidden');
}

// ── Cart Save to Wishlist (heart button below Remove) ────────

/**
 * Handles clicking the heart icon in cart items:
 * 1. Saves product to wishlist
 * 2. Removes item from cart via AJAX
 */
async function handleCartSave(btn) {
  const productId = btn.dataset.wishlistProductId;
  const isCartPage = !btn.closest('cart-drawer');

  addToWishlist(productId, {
    handle: btn.dataset.wishlistProductHandle,
    title: btn.dataset.wishlistProductTitle,
    image: btn.dataset.wishlistProductImage,
    price: parseInt(btn.dataset.wishlistProductPrice, 10) || 0,
    url: btn.dataset.wishlistProductUrl,
    variantId: btn.dataset.wishlistVariantId,
  });
  updateUI();
  showToast(t('wishlistSavedFromCart'));
  await removeCartItem(btn.dataset.removeUrl, isCartPage);
}

/**
 * Removes a cart item via AJAX and refreshes cart UI.
 * In the drawer: triggers cart:refresh to re-render without closing.
 * On cart page: reloads the page.
 */
async function removeCartItem(removeUrl, isCartPage) {
  try {
    const url = new URL(removeUrl, window.location.origin);
    const lineKey = url.searchParams.get('id');
    if (lineKey) {
      const rootUrl = window.themeVariables?.routes?.root_url || '/';
      const fetchUrl = `${rootUrl}cart/change.js`.replace('//', '/');
      await fetch(fetchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });

      if (isCartPage) {
        setTimeout(() => window.location.reload(), 400);
      } else {
        // Trigger Impact theme's cart drawer refresh (drawer stays open)
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } else {
      window.location.href = removeUrl;
    }
  } catch {
    window.location.href = removeUrl;
  }
}

// ── Init ─────────────────────────────────────────────────────

function init() {
  // Single delegated click handler for everything
  document.addEventListener('click', (e) => {
    // ── Wishlist toggle (product pages) ──
    const toggle = e.target.closest('.wishlist-toggle');
    if (toggle) {
      e.preventDefault();
      const button = toggle.closest('wishlist-button');
      if (button) handleToggle(button);
      return;
    }

    // ── Wishlist page: Add to Cart ──
    const atcBtn = e.target.closest('[data-wishlist-atc]');
    if (atcBtn && !atcBtn.disabled) {
      e.preventDefault();
      handleAddToCart(atcBtn.dataset.wishlistAtc);
      return;
    }

    // ── Wishlist page: Remove card ──
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      handleRemoveFromPage(removeBtn.dataset.wishlistRemove);
      return;
    }

    // ── Cart: Save to Wishlist heart button ──
    const saveBtn = e.target.closest('[data-wishlist-cart-save]');
    if (saveBtn) {
      e.preventDefault();
      handleCartSave(saveBtn);
      return;
    }

    // ── Toast close ──
    const closeBtn = e.target.closest('.wishlist-toast__close');
    if (closeBtn) {
      const toast = closeBtn.closest('wishlist-toast');
      if (toast) {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.setAttribute('hidden', ''), 400);
      }
      return;
    }

  });

  // ── Dropdown: window-level capture handlers (fire BEFORE FocusTrap on document) ──
  // Blocks touchstart/mousedown/pointerdown from reaching FocusTrap when
  // the user is interacting with the dropdown (outside the cart drawer).
  for (const evt of ['mousedown', 'pointerdown', 'touchstart', 'touchend']) {
    window.addEventListener(evt, (e) => {
      if (isDropdownOpen() && _dropdown && _dropdown.contains(e.target)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  // Handle dropdown button clicks at window level (capture, before FocusTrap).
  // Also closes dropdown on outside click.
  window.addEventListener('click', async (e) => {
    if (!isDropdownOpen()) return;

    // Click outside dropdown — close it (unless it's on the Remove link itself, which has its own handler)
    if (!_dropdown || !_dropdown.contains(e.target)) {
      if (!e.target.closest('[data-wishlist-cart-remove]')) hideDropdown();
      return;
    }

    // Click inside dropdown — prevent FocusTrap from seeing it
    e.stopPropagation();
    e.stopImmediatePropagation();

    const btn = e.target.closest('[data-action]');
    if (!btn || !_dropdownData) return;

    e.preventDefault();
    const data = _dropdownData;
    hideDropdown();

    if (btn.dataset.action === 'save') {
      addToWishlist(data.productId, {
        handle: data.handle,
        title: data.title,
        image: data.image,
        price: parseInt(data.price, 10) || 0,
        url: data.url,
        variantId: data.variantId,
      });
      updateUI();
      showToast(t('wishlistSavedFromCart'));
    }

    // Remove item then refresh cart — keep drawer open
    await removeCartItem(data.removeUrl, data.isCartPage);
  }, true);

  // Cart: intercept Remove links — capture phase so we fire before Impact's handlers
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-wishlist-cart-remove]');
    if (!link) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    // Toggle: if already open, just close
    if (isDropdownOpen()) {
      hideDropdown();
      return;
    }

    showDropdown(link, {
      productId: link.dataset.wishlistProductId,
      handle: link.dataset.wishlistProductHandle,
      title: link.dataset.wishlistProductTitle,
      image: link.dataset.wishlistProductImage,
      price: link.dataset.wishlistProductPrice,
      url: link.dataset.wishlistProductUrl,
      variantId: link.dataset.wishlistVariantId,
      removeUrl: link.href,
      isCartPage: !link.closest('cart-drawer'),
    });
  }, true);

  // Close dropdown on scroll (cart drawer scrolls)
  document.addEventListener('scroll', () => {
    if (isDropdownOpen()) hideDropdown();
  }, true);

  // Close dropdown on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isDropdownOpen()) hideDropdown();
  });

  updateUI();
  syncWithServer(); // Sync with server (lazy, 1.5s delay, logged-in users only)
  renderWishlistPage();
}

// Start
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
